import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { alertActions } from "../_actions";
import { useNavigate } from "react-router-dom";
import {
  HelpOutline,
  SaveAs,
  VpnKeyOutlined,
  TextSnippetOutlined,
  Description,
  ContactsOutlined,
  ContentPasteGoOutlined,
  BookOutlined,
  ContentPasteOutlined,
  HouseOutlined,
  InventoryOutlined,
  ContactMailOutlined
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import PageLoaderComp from "../component/pageLoaderComp";
import axios from "axios";
const DocumentStatus = ({ uploadedFile }) => {
  const getStatusStyles = () => {
    if (!uploadedFile) {
      return "text-[#7d7c7c]";
    }
    switch (uploadedFile?.status) {
      case 3:
        return "text-[#ff0000]"; // Red for rejected
      case 2:
        return "text-[#008000]"; // Green for validated
      case 1:
        return "text-[#0000FF]"; // Blue for in progress
      default:
        return uploadedFile?.file ? "text-[#FFA500]" : "text-[#7d7c7c]"; // Orange for uploaded but not reviewed, Gray for pending
    }
  };
  const getStatustext = () => {
    if (!uploadedFile) {
      return "en attente";
    }
    switch (uploadedFile?.status) {
      case 3:
        return "Rejeté";
      case 2:
        return "Validé";
      case 1:
        return "en cours";
      default:
        return uploadedFile?.file ? "Téléchargé" : "en attente"; // Show 'Uploaded' if file exists but no status
    }
  };
  return (
    <div className="flex item-start sm:items-center sm:space-x-2">
      <span className={`flex item-start sm:items-center ${getStatusStyles()}`}>
        <span className="w-2 h-2 rounded-full bg-current mr-2" />
        {getStatustext()}
      </span>
    </div>
  );
};
const DocumentList = ({ documents, currentDossier, createurl, newdocuments }) => {
  const dispatch = useDispatch();
  const [isQuestionnaireExpanded, setIsQuestionnaireExpanded] = useState(false);
  const [isJustificatifsExpanded, setIsJustificatifsExpanded] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newDocName, setNewDocName] = useState("");
  const [newDocDescription, setNewDocDescription] = useState("");
  const [localDocuments, setLocalDocuments] = useState(documents);
  const [newlyAddedDocs, setNewlyAddedDocs] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState('');

  // Keep localDocuments in sync with documents prop and preserve uploaded files
  useEffect(() => {
    const newDocs = Array.from(newlyAddedDocs)
      .map((docName) => localDocuments.find((doc) => doc.name === docName)).filter(Boolean);

    // Preserve uploaded file information and handle newly added docs from API
    const docsWithUploads = documents.map(doc => ({ ...doc, uploaded_file: doc?.uploaded_file || null }));

    // Get newly added docs from API
    // console.log('doc.uploaded_file.isnewlyadded',newdocuments);
    const apiNewDocs = newdocuments.filter(doc => doc && doc.isnewlyadded === 'true')
      .map(doc => ({
        name: doc.docId,
        title: doc.file_title || doc.title,
        icon: '../images/icon/document8.png',
        formats: doc.formats,
        position: doc.position,
        status: doc.status,
        uploaded_file: doc,
        description: '',
        error: null
      }));

    // Combine all documents
    const combinedDocs = [
      ...docsWithUploads,
      ...newDocs,
      ...apiNewDocs
    ];
    //console.log('combinedDocs', combinedDocs);

    // Remove duplicates based on name
    const uniqueDocs = combinedDocs.reduce((acc, current) => {
      const x = acc.find(item => item.name === current.name);
      if (!x) {
        return acc.concat([current]);
      }
      return acc;
    }, []);

    setLocalDocuments(uniqueDocs);
    // Update newlyAddedDocs Set with docs from API
    setNewlyAddedDocs(new Set([
      ...Array.from(newlyAddedDocs),
      ...apiNewDocs.map(doc => doc.name)
    ]));
  }, [documents]);

  const handleFileChange = async (event, docId) => {
    const file = event.target.files[0];
    if (!file) return;

    let doc;
    try {
      setLoading(true);
      // Check both documents and localDocuments (which includes newly added docs)
      doc = documents.find(d => d.name === docId) ||
        (newlyAddedDocs.has(docId) && localDocuments.find(d => d.name === docId));
      if (!doc) { throw new Error('Document not found'); }
      
      // Create FormData and upload immediately
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_title', doc.title);
      formData.append('docId', docId);
      formData.append('dossierId', currentDossier.id);
      formData.append('isnewlyadded', newlyAddedDocs.has(docId));

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}api/prouser/dossiers-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        // Log the response to check the structure
        console.log('Upload response:', response.data);

        // Get filename from response - check both possible locations
        const filename = response.data.data?.filename || response.data.filename;
        console.log('Filename:', filename);
        
        if (!filename) {
          console.error('No filename in response:', response.data);
          throw new Error('No filename received from server');
        }

        // Update the document with upload info and initial status
        const updatedDoc = {
          ...doc,
          uploaded_file: {
            id:response?.data?.data?.id,
            file: JSON.stringify([{ filename: filename }]),
            status: 1, // Set status to 'en cours' after successful upload
            upload_date: new Date().toISOString()
          },
          error: null // Clear any previous errors
        };
        console.log('updatedDoc', updatedDoc);

        // Update local documents state with the uploaded file info
        setLocalDocuments(prev => prev.map(d => d.name === docId ? updatedDoc : d));
        
        // Store file in state
        setSelectedFiles(prev => ({ ...prev, [docId]: file }));
        
        dispatch(alertActions.success(`${doc.title} téléchargé avec succès`));
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = doc ? `Erreur lors du téléchargement de ${doc.title}` : 'Erreur lors du téléchargement';
      dispatch(alertActions.error(errorMessage));
      
      if (doc) {
        const updatedDoc = {
          ...doc,
          error: error.message,
          uploaded_file: null // Clear uploaded file on error
        };
        setLocalDocuments(prev => prev.map(d => d.name === docId ? updatedDoc : d));
        
        // Remove file from state if upload failed
        setSelectedFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[docId];
          return newFiles;
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUploadedFile = async (doc) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }
    console.log('doc-->',doc);

    try {
      setLoading(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}api/prouser/dossiers-upload/${currentDossier.id}/${doc.uploaded_file?.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        // Update the document state to remove the uploaded file
        const updatedDoc = {
          ...doc,
          uploaded_file: null,
          error: null
        };
        setLocalDocuments(prev => prev.map(d => d.name === doc.name ? updatedDoc : d));
        
        // Remove from selectedFiles if present
        setSelectedFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[doc.name];
          return newFiles;
        });

        dispatch(alertActions.success('Document supprimé avec succès'));
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      dispatch(alertActions.error(error.message || 'Erreur lors de la suppression du document'));
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setLoading(true);
      const formData = new FormData();
      formData.append('status', 2);
      formData.append('dossierId', currentDossier.id);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}api/prouser/dossiers-submit`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        dispatch(alertActions.success('Dossier soumis avec succès'));
        navigate('/prouser/mes-dossiers');
      }
    } catch (error) {
      console.error('Submit failed:', error);
      dispatch(alertActions.error('Erreur lors de la soumission du dossier'));
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleLinkClick = (e, url) => {
    e.preventDefault();
    setSelectedUrl(url);
    setShowConfirmModal(true);
  };
  const handleConfirm = () => {
    window.open(selectedUrl, '_blank');
    setShowConfirmModal(false);
  };
  const handleRemoveDocument = (docName) => {
    setLocalDocuments((prev) => prev.filter((doc) => doc.name !== docName));
    setNewlyAddedDocs((prev) => {
      const newSet = new Set(prev);
      newSet.delete(docName);
      return newSet;
    });
    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[docName];
      return newFiles;
    });
    dispatch(alertActions.success("Document supprimé avec succès"));
  };

  const formtypeCheck =(legalForm)=>
  {
    if(legalForm && (legalForm.toLowerCase().includes('sas') || legalForm.toLowerCase().includes('société par actions simplifiée'))) {
      return 'SAS';
    }
    if(legalForm && (legalForm.toLowerCase().includes('sasu') || legalForm.toLowerCase().includes('société par actions simplifiée unipersonnelle'))) {
      return 'SASU';
    }
    if(legalForm && (legalForm.toLowerCase().includes('sarl') || legalForm.toLowerCase().includes('société à responsabilité limitée'))) {
      return 'SARL';
    }
    if(legalForm && (legalForm.toLowerCase().includes('eurl') || legalForm.toLowerCase().includes('entreprise unipersonnelle à responsabilité limitée'))) {
      return 'EURL';
    }
    if(legalForm && (legalForm.toLowerCase().includes('sci') || legalForm.toLowerCase().includes('sc') || legalForm.toLowerCase().includes('société civile') || legalForm.toLowerCase().includes('société civile unipersonnelle'))) {
      return 'SCI';
    }
    return '';
  }
  const handleDownload = (url, name, format) => {
    const isAbsoluteUrl = url.startsWith('http://') || url.startsWith('https://');
    // If it's a relative URL, prepend the REACT_APP_PRO_URL
    
    const fullUrl = isAbsoluteUrl ? url : `${process.env.REACT_APP_PRO_URL}${url}`;
    // Create temporary link and trigger download
    const link = document.createElement("a");
    link.href = fullUrl;
    link.target = '_blank'; // Open in new tab if browser blocks download
    link.download = `${name}.${format.toLowerCase()}`; // Normalize extension

    // Append link to body, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //console.log('localDocuments',localDocuments);
  return (
    <>
      {loading ? <PageLoaderComp /> : ""}
      <div className="mt-8">
        {/* Questionnaire Accordion */}
        <div className="bg-white border rounded-lg mb-4">
          <div className="p-4 flex items-center justify-between border-b">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2 text-gray-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path
                  fillRule="evenodd"
                  d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-900 font-medium">Questionnaire</span>
            </div>
            <button
              className="text-red-600 text-xs sm:text-sm font-medium flex items-center"
              onClick={() =>setIsQuestionnaireExpanded(!isQuestionnaireExpanded)}>
              À modifier
              <svg
                className={`w-4 h-4 ml-1 transform transition-transform ${isQuestionnaireExpanded ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          {isQuestionnaireExpanded && (
            <div>
              <div className="p-4">
                <p className="text-[#1E1E1E] text-sm">
                  {(() => {
                    try 
                    {
                      const formData = typeof currentDossier?.formdata === 'string' ?JSON.parse(currentDossier.formdata) :currentDossier?.formdata;
                      if (currentDossier.formtype === 'SAS/SASU' ||currentDossier.formtype === 'sarl-eurl') 
                      { 
                        return `Création de ${formData.companyType.type.toUpperCase()} ${formData?.companyName?.name || ''}`;
                      }
                      else if(currentDossier.formtype === 'sci')
                      {
                        return `Création de SCI ${formData?.companyName?.name || ''}`;
                      }
                      else if(currentDossier.formtype === 'dissolution')
                      {
                        return `Dissolution de ${formData?.company?.name || ''}  ${formtypeCheck(formData?.legalForm?.type) || ''}  `;
                      }
                      else if(currentDossier.formtype === 'modification')
                      {
                        return `Modification de ${formData?.company?.companyName || ''} ${formtypeCheck(formData?.company?.legalForm) || ''}`;
                      }
                      else if(currentDossier.formtype === 'formalities')
                      {
                        return `Dépot des comptes de ${formData?.company?.name || ''} ${formtypeCheck(formData?.company?.legalForm) || ''}`;
                      }
                      
                      return '';
                    } catch (error) {
                      console.error('Error parsing formdata:', error);
                      return '';
                    }
                  })()}
                </p>
              </div>
              <div className="p-4 flex justify-center">
                <button
                  // onClick={() => window.open(createurl, "_blank")}
                  onClick={(e) => handleLinkClick(e, createurl)}
                  className="w-full sm:w-auto px-6 py-2.5 bg-[#2563EB] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Confirmer mes réponses
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Justificatifs Accordion */}
        <div className="bg-white border rounded-lg">
          <div className="p-4 flex items-center justify-between border-b">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2 text-gray-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-900 font-medium">Justificatifs</span>
            </div>
            <button
              className="text-red-500 text-xs sm:text-sm font-medium flex items-center"
              onClick={() =>
                setIsJustificatifsExpanded(!isJustificatifsExpanded)
              }
            >
              {isJustificatifsExpanded
                ? "Cliquez pour réduire"
                : "Cliquez pour afficher"}
              <svg
                className={`w-4 h-4 ml-1 transform transition-transform ${isJustificatifsExpanded ? "rotate-180" : ""
                  }`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {isJustificatifsExpanded && (
            <div className="p-4">
              <form>
                <div className="space-y-4">
                  {localDocuments.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center flex-col sm:flex-row justify-between p-2 sm:p-4 border rounded-lg"
                    >
                      <div className="flex item-start w-full sm:items-center space-x-4">
                        <img src={doc.icon} alt={doc.title} className="w-4 h-4" style={{ width: '45px', height: 'auto', float: 'left' }} />
                        <div className="flex-1">

                          <h4 className="text-left text-sm font-medium text-navy-900">
                            <div className="flex items-center gap-2">
                              <span>{doc.title}</span>
                              {doc.description && (
                                <Tooltip
                                  className="tooltipbg"
                                  title={doc.description}
                                  placement="top"
                                  arrow
                                  enterTouchDelay={0}
                                  leaveTouchDelay={1500}
                                >
                                  <HelpOutline
                                    className="text-[#27295c] cursor-help w-4 h-4"
                                  />
                                </Tooltip>
                              )}
                            </div>
                          </h4>
                          {(doc.name === "pouvoir" || doc.name === "declaration_no_conviction_legal_representative" || doc.name === "autre_document2") && (
                            <>
                              <div className="mt-2 space-x-2">
                                {doc.formats.map((format, formatIndex) => (
                                  <span
                                    key={formatIndex}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-50 text-blue-600 cursor-pointer hover:bg-blue-100"
                                    onClick={() => handleDownload(doc.url[formatIndex], doc.name, format)}
                                  >
                                    {format}
                                  </span>
                                ))}
                              </div>
                            </>
                          )}
                          {/* <span className="text-sm text-gray-500">{doc.description}</span> */}
                          {doc.error && (
                            <p className="text-sm text-red-500 mt-1">
                              {doc.error}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between w-full sm:space-x-4">
                        <DocumentStatus uploadedFile={doc.uploaded_file} />
                        {doc.uploaded_file && doc.uploaded_file.file && (
                          <>
                            {(() => {
                              const fileData = JSON.parse(doc.uploaded_file.file);
                              const fileUrl = fileData[0]?.filename ? 
                                `${process.env.REACT_APP_AWS_URL_STATIC_BUSYPLACE}assets/uploads/${fileData[0].filename}` :
                                '#';
                              return fileUrl !== '#' ? (
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-600"
                                >
                                  <svg
                                    className="w-5 h-5 mr-2 text-gray-600"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </a>
                              ) : '';
                            })()}
                          </>
                        )}
                        
                        {(newlyAddedDocs.has(doc.name) || doc.uploaded_file) && (
                          <button type="button"
                            onClick={() => doc.uploaded_file ? handleDeleteUploadedFile(doc) : handleRemoveDocument(doc.name)}
                            className="text-red-500 hover:text-red-600 flex items-center gap-1"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                            Supprimer
                          </button>
                        )}
                        <div className="relative flex items-center">
                          {/* {selectedFiles[doc.name] && (
                            <span className="ml-2 text-sm text-gray-600">
                              {selectedFiles[doc.name].name}
                            </span>
                          )} */}
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                            id={doc.name}
                            name={doc.name}
                            onChange={(e) => handleFileChange(e, doc.name)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            title="Upload file"
                          />

                          <div className="p-2 hover:bg-gray-100 rounded-full">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              className={`size-6 ${selectedFiles[doc.name] ? 'text-green-500' : ''}`}
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                strokeWidth="2"
                                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                              />
                            </svg>
                          </div>

                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 text-center">
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Progression</span>
                      <span>
                        {localDocuments.filter(doc => doc.uploaded_file || selectedFiles[doc.name]).length} /{" "}
                        {localDocuments.length} justificatifs uploadés
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-secondary transition-all duration-300 rounded-full"
                        style={{
                          width: `${(localDocuments.filter(doc => doc.uploaded_file || selectedFiles[doc.name]).length /
                              localDocuments.length) *
                            100
                            }%`,
                          transition: "width 0.3s ease-in-out",
                          minWidth:
                            localDocuments.filter(doc => doc.uploaded_file || selectedFiles[doc.name]).length > 0
                              ? "2rem"
                              : "0",
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(true)}
                      className="px-4 py-2 border rounded-lg transition-colors bg-white border-blue-500 text-blue-500 hover:bg-blue-50"
                    >
                      Joindre un justificatif
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`px-4 py-2 border rounded-lg transition-colors ${isSubmitting
                          ? "bg-gray text-white border-gray cursor-not-allowed"
                          : "bg-secondary border-blue-500 hover:bg-primary text-white"
                        }`}
                    >
                      {isSubmitting ? "Soumission en cours..." : "Soumettre pour validation"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Modal for adding new document */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  Ajout d'un justificatif supplémentaire
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="docName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="docName"
                    value={newDocName}
                    onChange={(e) => setNewDocName(e.target.value)}
                    maxLength={50}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {newDocName.length}/50
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="docDescription"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description
                  </label>
                  <input
                    type="text"
                    id="docDescription"
                    value={newDocDescription}
                    onChange={(e) => setNewDocDescription(e.target.value)}
                    placeholder="facultatif"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Create new document object
                      const newDoc = {
                        name: newDocName.toLowerCase().replace(/\s+/g, "_"),
                        title: newDocName,
                        //description: newDocDescription,
                        icon: "../images/icon/document8.png",
                        formats: [".PDF"],
                        position: 0,
                        url: [""],
                        status: "",
                        error: null,
                      };

                      // Add new document to the array
                      setLocalDocuments((prev) => [...prev, newDoc]);

                      // Mark document as newly added
                      setNewlyAddedDocs(
                        (prev) => new Set([...prev, newDoc.name])
                      );

                      // Reset form and close modal
                      setShowModal(false);
                      setNewDocName("");
                      setNewDocDescription("");

                      // Show success message
                      dispatch(
                        alertActions.success("Document ajouté avec succès")
                      );
                    }}
                    disabled={!newDocName.trim()}
                    className={`px-4 py-2 rounded-lg ${!newDocName.trim()
                        ? "bg-gray text-white cursor-not-allowed"
                        : "bg-secondary hover:bg-primary text-white"
                      }`}
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Confirmation</h2>
            <p className="text-gray-600 mb-6">Êtes-vous sûr de vouloir continuer vers la form de modification ?</p>
            <p className="text-gray-600 mb-6">Des frais supplémentaires peuvent s'appliquer si les modifications affectent les conditions de paiement d'origine.</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button onClick={handleConfirm} class="text-base font-semibold text-white flex items-center justify-center px-4 py-2 sm:w-auto w-full rounded-md bg-primary">
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentList;
